import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  ConfigurationDto,
  PriceBreakdownDto,
  ProductionSpeedStepDto,
} from '@/api-sdk';
import { iconChevronLeft } from '@/components/icons';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
} from '@/context';
import { MobileController, StoreController } from '@/controllers';
import {
  BREAKPOINT_EXTRA_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
  BREAKPOINT_SMALL_PX,
} from '@/settings';
import { configuratorStore } from '@/stores';
import { baseStyles, buttonStyles, loadingStyles } from '@/styles';
import {
  ActionEvent,
  DateType,
  IntentActionEvent,
  IntentToEvent,
  Mode,
} from '@/types';
import { elephantOrderDateTemplate, sendCTAClickEvent } from '@/utils';

import '@/components/configurator/production/fg-production-speed';
import '../../usps/fg-usps';

@customElement('fg-configurator-actions-top-bottom')
export class FgConfiguratorActionsTopBottom extends LitElement {
  private validationStoreController = new StoreController(
    this,
    configuratorStore,
    state => {
      this.showValidationMessage = state.showValidationMessage;
    },
  );

  /**
   * Boolean to check if the loading state is active, and render the loading state
   *
   * @property {boolean} loading
   */
  @property({ type: Boolean })
  loading = false;

  /**
   * Procebreakdown data from the api call, is used to render prices
   *
   * @property {PriceBreakdownDto} priceBreakDownData
   */
  @property({ type: Object })
  priceBreakDownData?: PriceBreakdownDto;

  /**
   * Delivery time string
   *
   * @property {string} expectedDeliveryTime
   */
  @property({ type: String })
  expectedDeliveryDate?: string | null;

  /**
   * Configuration {@link ConfigurationDto} data from the api call, is used to validate the CTA buttons if not set
   *
   * @property {ConfigurationDto} configuration
   */
  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  /**
   * Boolean to check if the quantity is validated
   *
   * @property {boolean} quantityValidated
   */
  @property({ type: Boolean })
  quantityValidated = false;

  /**
   * Boolean to check if there is an error and render the error message
   *
   * @property {boolean} error
   */
  @property({ type: Boolean })
  error = false;

  /**
   * Boolean to check if the product is unprinted
   */
  @property({ type: Boolean })
  unprinted = false;

  @property({ type: Object })
  productionSpeedStep?: ProductionSpeedStepDto | null;

  @state()
  staleProductionSpeedStep!: ProductionSpeedStepDto;

  @state()
  showValidationMessage = configuratorStore.getState().showValidationMessage;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  protected mobileController = new MobileController(this);

  protected willUpdate(_changedProperties: PropertyValues): void {
    if (
      _changedProperties.has('productionSpeedStep') &&
      this.productionSpeedStep
    ) {
      this.staleProductionSpeedStep = this.productionSpeedStep;
    }
  }

  protected render() {
    const { mode, editing, salable, usps, hideSubtotalZeroPrice } =
      this.configuratorContext;
    const { locale, layout } = this.globalContext;
    const mobileCollapsible =
      this.configuratorContext.mobileCollapsible !== '0';
    const { isMobile } = this.mobileController;

    return html`
      <section
        class="${classMap({
          'mobile-collapsible-wrapper':
            mobileCollapsible && layout === 'retail',
        })}"
      >
        <div class="top">
          ${this.productionSpeedStep || this.staleProductionSpeedStep
            ? html`
                <fg-production-speed
                  part=${layout}
                  .loading=${this.loading}
                  .options=${this.productionSpeedStep?.options ||
                  this.staleProductionSpeedStep.options}
                ></fg-production-speed>
              `
            : nothing}

          <div
            class="prices ${classMap({
              loading: this.loading,
              row: Boolean(
                !this.expectedDeliveryDate &&
                this.priceBreakDownData?.expectedDeliveryDate,
              ),
            })}"
          >
            ${Boolean(
              this.expectedDeliveryDate ||
              this.priceBreakDownData?.earliestDeliveryDate,
            ) && mode === Mode.DEFAULT
              ? elephantOrderDateTemplate(
                  this.expectedDeliveryDate,
                  locale,
                  DateType.DELIVERY,
                  true,
                  this.configuratorContext.hspEnabled,
                  this.priceBreakDownData?.earliestDeliveryDate,
                  true,
                )
              : nothing}
            <div>
              <div>
                <span class="no-blur">
                  <span>
                    ${mode === Mode.DEFAULT
                      ? t('labels.allInPrice')
                      : t('labels.indicationPrice')}
                  </span>
                  <span>${t('labels.incVat')}:</span>
                </span>
                <span>
                  <span
                    class="${classMap({
                      'total-big': mode === Mode.DEFAULT,
                      'total-small': mode !== Mode.DEFAULT,
                    })} total"
                  >
                    ${hideSubtotalZeroPrice &&
                    this.priceBreakDownData?.totals?.excludingVat?.value === '0'
                      ? '-'
                      : (this.priceBreakDownData?.totals?.excludingVat
                          ?.formatted ?? '-')}
                  </span>
                  <span>
                    ${this.priceBreakDownData?.totals?.includingVat
                      ?.formatted ?? '-'}
                  </span>
                </span>
              </div>
            </div>
          </div>

          ${this.priceBreakDownData
            ? html`
                <div class="price-details">
                  <button
                    class="button-link"
                    @click=${() =>
                      this.dispatchEvent(
                        new CustomEvent('open-price-details', {
                          composed: true,
                        }),
                      )}
                  >
                    ${t('actions.viewPriceDetails')}
                  </button>
                </div>
              `
            : nothing}
          <div class="button-group">
            ${(layout === 'retail' && !mobileCollapsible) ||
            layout === 'wholesale'
              ? html`
                  <button
                    class="button-previous"
                    @click=${() =>
                      this.dispatchEvent(
                        new CustomEvent('prev-step', { composed: true }),
                      )}
                  >
                    <div class="previous-icon">${iconChevronLeft}</div>
                    <span class="previous-text">${t('actions.previous')}</span>
                  </button>
                `
              : nothing}
            ${salable
              ? html`
                  <button
                    class="button button-cta
                  ${classMap({
                      'button-full-width':
                        layout === 'retail' && mobileCollapsible,
                    })}
                  "
                    @click=${() => this.getMobileCtaAction(mode)}
                  >
                    ${editing
                      ? t('actions.updateCart')
                      : html`
                          <span class="mobile-hidden">
                            ${t('actions.addToCart')}
                          </span>
                          <span class="mobile-only">
                            ${this.getMobileCtaLabel(mode)}
                          </span>
                        `}
                  </button>
                `
              : nothing}
          </div>

          ${this.error
            ? html`
                <p class="error">${t('errors.somethingWentWrong')}</p>
              `
            : nothing}
          ${!editing && salable && mode === Mode.DEFAULT
            ? html`
                <p class="digital-proof">
                  ${t('content.configurator.addToCart.digitalProof')}
                </p>
              `
            : nothing}
        </div>

        ${layout !== 'wholesale'
          ? html`
            <div
              class="bottom               
              ${classMap({
                center: editing || !salable,
                'mobile-collapsible': layout === 'retail' && mobileCollapsible,
              })}
              "
            >
              <fg-usps .usps=${usps} .type=${isMobile && mobileCollapsible ? 'mobile-collapsible' : 'desktop'}></fg-usps>
              <fg-configurator-actions-top-bottom-buttons
                .configuration=${this.configuration}
                .unprinted=${this.unprinted}
                .quantityValidated=${this.quantityValidated}
                class='top-bottom-buttons ${classMap({
                  'mobile-collapsible':
                    layout === 'retail' && mobileCollapsible,
                })}'
              ></fg-configurator-actions-top-bottom-buttons>
              </ul>
            </div>
          `
          : nothing}
      </section>
    `;
  }

  getMobileCtaAction(mode: Mode) {
    const { editing } = this.configuratorContext;

    if (mode === Mode.DEFAULT) {
      if (this.configuration && !this.showValidationMessage) {
        this.intentTo(
          editing ? 'intent-to-update-cart' : 'intent-to-add-to-cart',
        );
      }
      this.triggerValidationEvents(editing ? 'update-cart' : 'add-to-cart');
      const buttonTextKey = editing
        ? 'actions.updateCart'
        : 'actions.addToCart';
      sendCTAClickEvent('add-to-cart', buttonTextKey);
    }

    if (mode === Mode.DIGITAL_EXAMPLE) {
      if (this.configuration && !this.showValidationMessage) {
        this.intentTo('intent-to-request-print-proof');
      }
      this.triggerValidationEvents('request-print-proof');
      sendCTAClickEvent('add-to-print-proof', 'actions.requestPrintProof');
    }

    if (mode === Mode.QUOTE) {
      if (this.configuration && !this.showValidationMessage) {
        this.intentTo('intent-to-add-to-quotation');
      }
      this.triggerValidationEvents('add-to-quotation');
      sendCTAClickEvent('add-to-quotation', 'actions.addToQuotation');
    }
  }

  getMobileCtaLabel(mode: Mode) {
    switch (mode) {
      case Mode.DIGITAL_EXAMPLE:
        return t('actions.requestPrintProof');
      case Mode.QUOTE:
        return t('actions.addToQuotation');
      default:
        return t('actions.inCart');
    }
  }

  triggerValidationEvents(actionEvent: ActionEvent) {
    this.dispatchEvent(
      new CustomEvent<{ actionEvent: ActionEvent }>(
        'trigger-validation-events',
        {
          composed: true,
          detail: {
            actionEvent: actionEvent,
          },
        },
      ),
    );
  }

  /**
   * @function intentTo
   * @param {IntentActionEvent} eventName
   * @description Dispatches an event to fg-configurator-actions.ts to handle the intent-to events
   */
  intentTo(eventName: IntentActionEvent) {
    this.dispatchEvent(
      new CustomEvent<IntentToEvent>('intent-to', {
        composed: true,
        detail: {
          eventName: eventName,
        },
      }),
    );
  }

  static styles = [
    baseStyles,
    buttonStyles,
    loadingStyles,
    css`
      :host {
        display: block;
      }

      section > div {
        padding: 20px;
      }

      .top {
        background-color: #ffffff;
        border-top-right-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
        border-top-left-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      fg-production-speed {
        display: block;
        margin-bottom: 1rem;
      }

      .prices {
        text-align: right;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        row-gap: 10px;
      }

      .prices.row {
        flex-direction: row;
      }

      .top-bottom-buttons {
        display: block;
        flex-grow: 1;
        width: calc((100% / 7) * 4);
      }

      .prices > div:not(.delivery-block-container):first-child {
        text-align: right;
        padding-top: 5px;
      }

      .prices > div:not(.delivery-block-container):last-child {
        display: flex;
        justify-content: flex-end;
      }

      .prices div:last-child div,
      .prices div:last-child div > span {
        display: flex;
      }

      .prices div:last-child div > span {
        align-items: end;
        flex-direction: column;
        justify-content: center;
      }

      .prices div:last-child div > span:last-child {
        margin-left: 10px;
      }

      .prices div:last-child div > span span:first-child {
        margin-bottom: 5px;
      }

      .total-small {
        font-size: 1.125rem;
      }

      .total-big {
        font-size: 1.5rem;
      }

      .total {
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );

        font-weight: 700;
      }

      .price-details {
        font-size: 0.875rem;
        display: flex;
        margin-top: 5px;
        justify-content: flex-end;
      }

      .button-cta {
        width: 100%;
        grid-column: span 2 / span 2;
      }

      .button-cta .mobile-hidden {
        display: none;
      }

      .button-cta .mobile-only {
        display: block;
      }

      .button-previous {
        display: flex;
        align-items: center;
      }

      .previous-text {
        color: var(--fg-primary, var(--fg-fallback-primary));
        font-size: 16px;
        font-weight: 500;
        margin-left: 4px;
      }

      .previous-icon {
        fill: var(--fg-primary, var(--fg-fallback-primary));
        color: var(--fg-primary, var(--fg-fallback-primary));
        height: 18px;
        width: 9px;
        display: flex;
      }

      .button-group {
        gap: 8px;
        margin-top: 20px;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
      }

      .digital-proof {
        text-align: center;
        margin-top: 20px;
      }

      .bottom {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-bottom-right-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
        border-bottom-left-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
        display: none;
      }

      .bottom.mobile-collapsible {
        display: flex;
      }

      .actions {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        gap: 10px;
      }

      .actions .button {
        width: 100%;
        white-space: nowrap;
        padding: 0 15px;
      }

      .error {
        font-size: 0.875rem;
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
        text-align: center;
        margin-top: 15px;
      }

      .info-delivery-date {
        color: var(--fg-primary, var(--fg-fallback-primary));
      }

      .expected-delivery-content {
        display: flex;
        gap: 4px;
      }

      .expected-delivery-content .info-and-formatted-date {
        height: fit-content;
        display: flex;
        gap: 4px;
      }

      .delivery-block-content {
        flex-direction: column;
      }

      .delivery-block-container {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        padding: 16px;
        border-radius: 8px;
        text-align: left;
      }

      .expected-delivery-date {
        display: flex;
        gap: 4px;
        height: fit-content;
      }

      .expected-delivery-container {
        display: inline-flex;
        gap: 4px;
      }

      @media screen and (max-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .expected-delivery-container {
          align-self: end;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        .mobile-collapsible-wrapper {
          border: 1px solid var(--fg-gray--300, var(--fg-falback-gray--300));
          border-radius: var(
            --fg-borderradius,
            var(--fg-fallback-borderradius)
          );
          margin-top: 12px;
        }

        .mobile-collapsible-wrapper
          .prices
          > div:not(.delivery-block-container):last-child {
          display: block;
        }

        .mobile-collapsible-wrapper
          .prices
          div:last-child
          div
          span:first-of-type {
          display: block;
          text-align: left;
          width: 100%;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_SMALL_PX - 1}px) {
        section > div {
          padding: 12px;
        }

        .bottom {
          flex-direction: column;
        }

        .button-full-width {
          grid-column: span 3 / span 3;
        }
        .top-bottom-buttons.mobile-collapsible {
          width: 100%;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .button-cta {
          grid-column: span 3 / span 3;
          font-size: 1.125rem;
          height: 50px;
        }

        .button-cta .mobile-hidden {
          display: block;
        }

        .button-cta .mobile-only {
          display: none;
        }

        .button-previous {
          display: none;
        }

        .prices {
          flex-direction: column;
        }

        .prices div:last-child div {
          column-gap: 10px;
          justify-content: end;
        }

        .bottom {
          align-items: center;
          justify-content: space-between;
        }

        .bottom.center {
          justify-content: center;
        }

        .bottom.unprinted {
          align-items: unset;
        }

        fg-usps {
          display: block;
          order: 1;
          width: calc((100% / 7) * 3);
        }

        .price-details {
          justify-content: flex-end;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        :host {
          margin-top: 16px;
          font-size: 1rem;
          border: 1px solid var(--fg-gray--300, var(--fg-falback-gray--300));
          border-radius: var(
            --fg-borderradius,
            var(--fg-fallback-borderradius)
          );
        }

        .bottom {
          gap: 20px;
          display: flex;
        }

        .usp {
          display: block;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .bottom {
          gap: 24px;
        }

        .prices {
          display: flex;
          flex-direction: row;
          column-gap: 24px;
        }

        .prices {
          display: flex;
          flex-direction: row;
          column-gap: 24px;
        }

        .prices > div:only-child {
          margin-left: auto;
        }

        .prices > div:first-child {
          text-align: left;
        }

        .prices > div:first-child strong {
          white-space: nowrap;
        }

        .prices div:last-child div > span span:first-child {
          width: max-content;
        }
      }
    `,
  ];
}
